import axios from 'axios';

export type InsightsHubSortBy = 'relevant' | 'recent';

const SORT_BY_MAP: Record<InsightsHubSortBy, string> = {
  // Coarse relevance buckets, then articles above research updates, then newer first.
  relevant: '_text_match(buckets: 10):desc,_eval(type:article):desc,published_date:desc',
  recent: 'published_date:desc',
};

type Options = {
  q: string;
  query_by?: string[];
  page?: number;
  per_page?: number;
  projectTypes?: string[];
  sortBy?: InsightsHubSortBy;
  /** Category tag(s). Filters by tag instead of matching the text. */
  category?: string[];
  /** Drops research updates, which otherwise outrank articles by date. */
  onlyArticles?: boolean;
};

export async function makeIndexedInsightsHubSearchRequest({
  q,
  page,
  per_page,
  query_by,
  projectTypes,
  sortBy,
  category,
  onlyArticles,
}: Options) {
  const endpoint =
    process.env.IS_PRODUCTION_APP === 'true'
      ? '/collections/articles/documents/search/'
      : '/collections/articles-draft/documents/search/';

  const payload: Record<string, string> = {
    q,
    query_by: query_by?.join(',') ?? 'title,category,content,summary,preview',
    per_page: `${per_page ?? '20'}`,
    page: `${page ?? '1'}`,
    sort_by: SORT_BY_MAP[sortBy ?? 'relevant'],
    query_by_weights: '4,3,1,1,1',
    prioritize_exact_match: 'true',
  };

  const filters: string[] = [];

  if (projectTypes && projectTypes.length > 0) {
    // Match Strapi: (visibility != subscriber_only) OR (subscriber_only with null/matching project_types)
    // Using num_project_types:=0 to check for empty project_types array
    const projectTypesStr = projectTypes.map((type) => `"${type}"`).join(',');
    // The visibility filter mixes || and &&, so it is parenthesised before the
    // rest is anded on.
    filters.push(
      `(is_subscriber_only:false || (is_subscriber_only:true && (num_project_types:=0 || project_type:[${projectTypesStr}])))`
    );
  }

  if (category?.length) {
    filters.push(`category:=[${category.join(',')}]`);
  }

  if (onlyArticles) {
    filters.push('type:=article');
  }

  if (filters.length) {
    payload.filter_by = filters.join(' && ');
  }

  const queryString = new URLSearchParams(payload).toString();

  const res = await axios.get(`${process.env.TYPESENSE_URL}${endpoint}?${queryString}`, {
    headers: {
      'X-TYPESENSE-API-KEY': process.env.TYPESENSE_API_KEY,
    },
  });

  return res.data?.hits;
}
